<template>
    <a-card :title="$t('screenTitle.transferToTLBB')" :loading="!isReady">
        <a-alert v-if="!pointToCoinRate" :message="$t('message.fetchError')" type="error" show-icon>
            <template #description>
                <a-button type="primary" @click="fetchRate">{{ $t('text.reload') }}</a-button>
            </template>
        </a-alert>

        <div v-else>
            <a-alert v-if="userInfo?.point" :message="`Bạn đang có ${number_format(userInfo?.point)} ${$t('labelText.user_point')} chưa nhận trong Game`" class="mb-20" show-icon />

            <a-alert v-if="(!userInfo?.coin && !userInfo?.promotion_coin) || (!Math.floor((userInfo?.coin || 0) / pointToCoinRate) && !Math.floor((userInfo?.promotion_coin || 0) / pointToCoinRate))" message="Tài khoản không đủ tiền để chuyển vào game" type="warning" show-icon>
                <template #description>
                    <div>Tài khoản bạn không còn đủ {{ $t('labelText.user_point') }} để thực hiện hành động này.</div>
                    <!--<router-link :to="{ name: RouteName.PAYMENT_CHARGE_VIA_EBANK}">
                        <a-button type="primary">{{ $t('text.chargeNow') }}</a-button>
                    </router-link>-->
                </template>
            </a-alert>

            <a-form v-else ref="form" :model="formModel" @finish="onFinish" :rules="formRule" :scrollToFirstError="true" :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
                <a-form-item v-if="Math.floor((userInfo?.coin || 0) / pointToCoinRate)" :label="$t('labelText.user_coin')" name="coin" :help="formModel.coin ? `${$t('text.preview')}: ${number_format(formModel.coin)}` : null">
                    <a-input-number v-model:value="formModel.coin" :min="0" :max="userInfo?.coin">
                        <template #addonAfter>
                            <a-button size="small" type="ghost" @click="() => { formModel.coin = userInfo?.coin as number }">{{ $t('text.max') }}</a-button>
                        </template>
                    </a-input-number>
                </a-form-item>

                <a-form-item v-if="Math.floor((userInfo?.promotion_coin || 0) / pointToCoinRate)" :label="$t('labelText.user_promotion_coin')" name="promotion_coin" :help="formModel.promotion_coin ? `${$t('text.preview')}: ${number_format(formModel.promotion_coin)}` : null">
                    <a-input-number v-model:value="formModel.promotion_coin" :min="0" :max="userInfo?.promotion_coin">
                        <template #addonAfter>
                            <a-button size="small" type="ghost" @click="() => { formModel.promotion_coin = userInfo?.promotion_coin as number }">{{ $t('text.max') }}</a-button>
                        </template>
                    </a-input-number>
                </a-form-item>

                <a-form-item :wrapper-col="{ lg: {span: 20, offset: 4}, md: {span:24, offset: 0}}">
                    <a-button type="primary" html-type="submit" :loading="isLoading" :disabled="!(formModel.coin + formModel.promotion_coin)">{{ $t('text.transfer') }}</a-button>
                </a-form-item>
            </a-form>
        </div>
    </a-card>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref, watch, } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { Alert, Button, Card, Form, InputNumber, message, Modal, } from "ant-design-vue";

import { RouteName } from "@/router";
import ApiRequest from "@/utils/ApiRequest";
import { UserRole } from "@/store/modules/user";
import { number_format } from "@/utils/helpers";
import { StoreType as UserStoreType, UserInfo } from '@/store/modules/user';
import { RuleObject } from "ant-design-vue/lib/form";

interface FormModel {
    coin: number,
    promotion_coin: number,
}

export default defineComponent({
    components: {
        ACard: Card,
        AAlert: Alert,
        AButton: Button,
        AInputNumber: InputNumber,
        AForm: Form,
        AFormItem: Form.Item,
    },

    setup() {
        const { t } = useI18n();
        const store = useStore();

        const isReady = ref(false);
        const pointToCoinRate = ref<number>();
        const userInfo = ref<UserInfo>();

        const fetchRate = async () => {
            isReady.value = false;

            return store.dispatch(UserStoreType.FETCH_USER)
                .then(async () => {
                    return ApiRequest.get('/payments/point-to-coin-rate')
                        .then(({ data }) => {
                            pointToCoinRate.value = data.data;
                        })
                })
                .finally(() => {
                    isReady.value = true;
                });
        };

        const form = ref<typeof Form>();

        const formModel = reactive<FormModel>({
            coin: 0,
            promotion_coin: 0,
        });

        const formExtraError: Record<keyof FormModel, string> = {
            coin: '',
            promotion_coin: '',
        };
        const formRule = reactive<Record<keyof FormModel, RuleObject[]>>({
            coin: [
                {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    validator: (_rule: any, _value, callback) => callback(formExtraError[_rule.field as keyof FormModel] || undefined),
                },
            ],
            promotion_coin: [
                {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    validator: (_rule: any, _value, callback) => callback(formExtraError[_rule.field as keyof FormModel] || undefined),
                },
            ],
        });

        const isLoading = ref(false);

        const onFinish = (values: FormModel) => {
            Modal.confirm({
                title: t('text.confirm'),
                maskClosable: true,
                onOk: async () => {
                    isLoading.value = true;

                    return ApiRequest.post('/payments/transfer-to-tlbb', values)
                        .then(() => {
                            message.success(t('message.success'));

                            form.value?.resetFields();
                        })
                        .catch(err => {
                            ApiRequest.handleError(err, formExtraError, form.value);
                        })
                        .finally(() => {
                            isLoading.value = false;

                            fetchRate();
                        });
                }
            });

        }

        watch(() => store.getters.user, (newValue) => {
            userInfo.value = newValue;
        });

        onMounted(() => {
            fetchRate();
        });

        return {
            isLoading,
            isReady,

            number_format,

            fetchRate,

            userInfo,

            pointToCoinRate,

            formModel,
            formRule,
            formExtraError,
            form,
            onFinish,

            RouteName,

            UserRole,
        };
    },
})
</script>

<style scoped>

</style>
